import React from "react";

// Base URL for machha character assets (served via CloudFront CDN)
const BASE_URL =
  "https://d3ic3ju44zc444.cloudfront.net/assets";

type AssetCategory = "NewCharacters" | "LandingPageAssets" | "LogoImages";

function getCategoryFolder(cat: AssetCategory): string {
  if (cat === "LandingPageAssets") return "LandingPageAssets";
  if (cat === "LogoImages") return "LogoImages";
  return "NewCharacters";
}

type CharacterState =
  | "Confirmation"
  | "Empty"
  | "Error"
  | "Failure"
  | "Forbidden"
  | "Loading"
  | "Maintanance"
  | "Milestone"
  | "Not Found"
  | "Offline"
  | "Onboarding"
  | "Rate Limit"
  | "Success"
  | "Warning"
  | "Welcome";

type Density = 1 | 2 | 3 | 4;

function getDeviceDensity(): Density {
  if (typeof window === "undefined") return 2;
  const dpr = window.devicePixelRatio || 1;
  if (dpr >= 3.5) return 4;
  if (dpr >= 2.5) return 3;
  if (dpr >= 1.5) return 2;
  return 1;
}

/**
 * Get the URL for a machha character asset at the appropriate density.
 */
export function getMachhaAsset(
  category: AssetCategory,
  name: string,
  density?: Density,
): string {
  const d = density || getDeviceDensity();
  const suffix = `@${d}x`;
  const fileName = `${name}${suffix}.png`;
  return `${BASE_URL}/${encodeURIComponent(getCategoryFolder(category))}/${encodeURIComponent(fileName)}`;
}

/**
 * Get a srcSet string for responsive image loading.
 */
export function getMachhaAssetSrcSet(
  category: AssetCategory,
  name: string,
): string {
  return [1, 2, 3, 4]
    .map((d) => {
      const fileName = `${name}@${d}x.png`;
      return `${BASE_URL}/${encodeURIComponent(getCategoryFolder(category))}/${encodeURIComponent(fileName)} ${d}x`;
    })
    .join(", ");
}

// ─── Fallback image ───────────────────────────────────────────────────────────
// Processed from assets/NewImages/fallback_empty_image.png via:
//   ./scripts/add-asset.sh assets/NewImages/fallback_empty_image.png LandingPageAssets
//
// Exported so other components (e.g. Navbar motion.img) can use it directly.
export const MACHHA_FALLBACK_SRC = getMachhaAsset(
  "LandingPageAssets",
  "fallback_empty_image",
);
export const MACHHA_FALLBACK_SRCSET = getMachhaAssetSrcSet(
  "LandingPageAssets",
  "fallback_empty_image",
);

// ─── AssetImg ─────────────────────────────────────────────────────────────────
// Implements the HTML-only <object> fallback technique:
//   https://dev.to/albertodeago88/html-only-image-fallback-19im
//
// Structure:
//   <object data="primary.png">   ← loads primary; browser renders inner content on failure
//     <img src="fallback.png" />  ← only rendered when primary fails (no JS needed)
//   </object>
//
// Fade-in: the component stays at opacity 0 until either the primary (object onLoad)
// or the fallback (inner img onLoad) fires — then fades in at 0.35s ease.
// This prevents the top-to-bottom progressive paint artifact on large PNGs.
//
// Note: <object> does not support srcSet or loading="lazy". Density is resolved
// upfront by getMachhaAsset() based on window.devicePixelRatio.

interface AssetImgProps {
  src: string;
  alt: string;
  className?: string;
  style?: React.CSSProperties;
  draggable?: boolean;
}

export function AssetImg({
  src,
  alt,
  className = "",
  style,
  draggable = false,
}: AssetImgProps): React.ReactElement {
  const [loaded, setLoaded] = React.useState(false);
  const handleLoad = () => setLoaded(true);

  return React.createElement(
    "object",
    {
      data: src,
      type: "image/png",
      className,
      // aria-label carries the accessible name since <object> has no alt
      "aria-label": alt,
      style: {
        ...style,
        opacity: loaded ? 1 : 0,
        transition: "opacity 0.35s ease",
        // Match <img> inline layout behaviour
        display: "inline-block",
      },
      onLoad: handleLoad,
    },
    // ── Fallback ──────────────────────────────────────────────────────────────
    // Rendered by the browser only when `data` fails to load.
    // The inner img's onLoad triggers the same fade-in so the transition
    // plays correctly on the fallback path too.
    React.createElement("img", {
      src: MACHHA_FALLBACK_SRC,
      srcSet: MACHHA_FALLBACK_SRCSET,
      alt,
      draggable,
      style: {
        display: "block",
        width: "100%",
        height: "100%",
        objectFit: "contain",
      },
      onLoad: handleLoad,
    }),
  );
}

// ─── MachhaCharacter ──────────────────────────────────────────────────────────

interface MachhaCharacterProps {
  state: CharacterState;
  /**
   * Which part to render.
   * - 'body' (default): body image only — use for large areas ≥ 120px
   * - 'face': face only — use for small spaces, panels (60–100px), and inline (24–48px)
   */
  part?: "body" | "face";
  /** Size in pixels. For 'body', this is the width (height = size * 1.4). For 'face', square. */
  size?: number;
  className?: string;
  bodyVariant?: number;
  faceVariant?: number;
}

/**
 * Renders a single Machha character image (body or face).
 * Falls back to MACHHA_FALLBACK_SRC if the character image cannot be fetched.
 */
export function MachhaCharacter({
  state,
  part = "body",
  size = 200,
  className = "",
  bodyVariant,
  faceVariant,
}: MachhaCharacterProps) {
  const [loaded, setLoaded] = React.useState(false);
  const handleLoad = () => setLoaded(true);

  const fadeStyle: React.CSSProperties = {
    opacity: loaded ? 1 : 0,
    transition: "opacity 0.35s ease",
  };

  if (part === "face") {
    const faceSuffix = faceVariant ? ` ${faceVariant}` : "";
    const faceName = `${state} Face${faceSuffix}`;
    const faceNameAlt = `${state} face${faceSuffix}`;
    const faceSrc = getMachhaAsset("NewCharacters", faceName);
    const faceSrcSet = getMachhaAssetSrcSet("NewCharacters", faceName);

    return React.createElement("img", {
      src: faceSrc,
      srcSet: faceSrcSet,
      alt: `${state} character`,
      className: `object-contain ${className}`,
      style: { width: size, height: size, ...fadeStyle },
      loading: "lazy",
      onLoad: handleLoad,
      onError: (e: React.SyntheticEvent<HTMLImageElement>) => {
        const target = e.target as HTMLImageElement;
        if (target.dataset.fallback) return;
        if (!target.dataset.retried) {
          target.dataset.retried = "true";
          target.src = getMachhaAsset("NewCharacters", faceNameAlt);
          target.srcset = getMachhaAssetSrcSet("NewCharacters", faceNameAlt);
        } else {
          target.dataset.fallback = "true";
          target.src = MACHHA_FALLBACK_SRC;
          target.srcset = MACHHA_FALLBACK_SRCSET;
        }
      },
    });
  }

  const bodySuffix = bodyVariant ? ` ${bodyVariant}` : "";
  const bodyName = `${state} Body${bodySuffix}`;
  const bodySrc = getMachhaAsset("NewCharacters", bodyName);
  const bodySrcSet = getMachhaAssetSrcSet("NewCharacters", bodyName);

  return React.createElement("img", {
    src: bodySrc,
    srcSet: bodySrcSet,
    alt: `${state} character`,
    className: `object-contain ${className}`,
    style: { width: size, height: size * 1.4, ...fadeStyle },
    loading: "lazy",
    onLoad: handleLoad,
    onError: (e: React.SyntheticEvent<HTMLImageElement>) => {
      const target = e.target as HTMLImageElement;
      if (target.dataset.fallback) return;
      target.dataset.fallback = "true";
      target.src = MACHHA_FALLBACK_SRC;
      target.srcset = MACHHA_FALLBACK_SRCSET;
    },
  });
}
